import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Lock, Fingerprint, CreditCard, CheckCircle2, ArrowLeft, ArrowRight, Smartphone, Globe, Wallet, FileText, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import BrandMark from '@/components/Brand';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatSAR, formatDate } from '@/lib/format';
import { usePageTitle } from '@/lib/usePageTitle';
import { cn } from '@/lib/utils';

const CASE = {
  caseId: 'CASE-10412',
  debtor: 'Abu Fahad',
  client: 'Saudi Retail Bank — Synthetic',
  product: 'Personal Finance',
  amount: 18450,
  delinquencyDays: 94,
};

export default function DebtorPortal() {
  usePageTitle('Pay your account');
  const navigate = useNavigate();
  const [step, setStep] = useState('verify'); // verify | options | plan | review | done
  const [code, setCode] = useState(47);
  const [verifyState, setVerifyState] = useState('idle'); // idle | sent | confirmed
  const [option, setOption] = useState('plan');
  const [installments, setInstallments] = useState(6);
  const [downPct, setDownPct] = useState(10);
  const monthly = Math.round(((CASE.amount * (1 - downPct / 100)) / installments));
  const downPayment = Math.round(CASE.amount * (downPct / 100));

  const sendNafath = () => {
    setVerifyState('sent');
    setCode(Math.floor(Math.random() * 90 + 10));
    setTimeout(() => setVerifyState('confirmed'), 2200);
    toast.message('Nafath challenge sent to your registered device', { description: 'Open the Nafath app and confirm' });
  };

  return (
    <div dir="ltr" className="min-h-screen bg-gradient-ivory">
      {/* Top header */}
      <header className="border-b border-border bg-onyx text-background pattern-arabesque">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <BrandMark tone="ivory" />
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10">Debtor self-service • demo</Badge>
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-background/80 hover:text-background hover:bg-white/10">
              <ArrowLeft className="h-4 w-4 mr-1.5" />Back to app
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10">
        {/* Stepper */}
        <ol className="flex items-center gap-2 mb-8 text-[11px] text-muted-foreground">
          {[{ k: 'verify', l: 'Verify identity' }, { k: 'options', l: 'Choose option' }, { k: 'plan', l: 'Configure plan' }, { k: 'review', l: 'Review & pay' }, { k: 'done', l: 'Done' }].map((s, i, arr) => {
            const order = ['verify', 'options', 'plan', 'review', 'done'];
            const idx = order.indexOf(step);
            const current = idx === i;
            const done = idx > i;
            return (
              <li key={s.k} className="flex items-center gap-2">
                <span className={cn('h-6 w-6 rounded-full flex items-center justify-center font-semibold', done ? 'bg-success text-success-foreground' : current ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>{done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}</span>
                <span className={cn(current ? 'text-foreground font-medium' : '')}>{s.l}</span>
                {i < arr.length - 1 && <span className="h-px w-8 bg-border mx-1" />}
              </li>
            );
          })}
        </ol>

        <AnimatePresence mode="wait">
          {step === 'verify' && (
            <motion.div key="verify" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="grid lg:grid-cols-5 gap-6">
              <div className="lg:col-span-3 surface-card p-6">
                <p className="text-[10px] uppercase tracking-widest text-primary">Step 1 • Verify identity</p>
                <h2 className="text-2xl font-semibold mt-1">Sign in with Nafath</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-md">Enter your National ID or Iqama. We will send a Nafath challenge to your registered device for secure verification.</p>
                <div className="mt-5 space-y-3 max-w-md">
                  <div>
                    <label className="text-xs text-muted-foreground">National ID / Iqama</label>
                    <Input placeholder="1••••••••42" className="font-mono-num" defaultValue="1••••••••42" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Mobile</label>
                    <Input placeholder="+9665•••••••••" className="font-mono-num" defaultValue="+9665••••••7•••" />
                  </div>
                  {verifyState === 'idle' && (
                    <Button onClick={sendNafath} className="bg-primary text-primary-foreground hover:bg-primary/90 w-full mt-2">
                      <Fingerprint className="h-4 w-4 mr-1.5" />Send Nafath challenge
                    </Button>
                  )}
                  {verifyState === 'sent' && (
                    <div className="surface-card !bg-info/10 !border-info/30 p-4">
                      <p className="text-sm font-medium text-info">Approve in Nafath app</p>
                      <p className="text-xs text-muted-foreground mt-1">Select number <span className="font-mono-num text-2xl text-foreground mx-1">{code}</span> on your device.</p>
                    </div>
                  )}
                  {verifyState === 'confirmed' && (
                    <div className="surface-card !bg-success/10 !border-success/40 p-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-success" />
                        <p className="text-sm font-medium text-success">Identity confirmed</p>
                      </div>
                      <Button onClick={() => setStep('options')} className="bg-success text-success-foreground hover:bg-success/90">Continue<ArrowRight className="h-4 w-4 ml-1.5" /></Button>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-6 flex items-center gap-1.5"><Lock className="h-3 w-3" />Your data is masked and processed under Saudi PDPL. No real Nafath connection is used in this prototype.</p>
              </div>

              <aside className="lg:col-span-2 space-y-4">
                <div className="surface-card p-5">
                  <p className="text-[10px] uppercase tracking-widest text-primary">Why this matters</p>
                  <ul className="mt-3 space-y-2.5 text-sm">
                    {[
                      ['Secure verification via national identity', ShieldCheck],
                      ['Encrypted in transit & at rest', Lock],
                      ['Audit-grade evidence trail', FileText],
                      ['Multi-channel access — web & mobile', Smartphone],
                    ].map(([t, I]) => (
                      <li key={t} className="flex items-start gap-2"><I className="h-4 w-4 text-primary mt-0.5" /><span>{t}</span></li>
                    ))}
                  </ul>
                </div>
                <div className="surface-card p-5 bg-gradient-to-br from-onyx to-onyx/95 text-background">
                  <p className="text-[10px] uppercase tracking-widest text-primary">Need help?</p>
                  <p className="text-sm mt-1 text-background/85">Our team is available 09:00–21:00 KSA on WhatsApp.</p>
                  <Button size="sm" variant="outline" className="mt-3 border-white/20 text-background hover:bg-white/10">
                    <MessageSquare className="h-4 w-4 mr-1.5" />Chat on WhatsApp
                  </Button>
                </div>
              </aside>
            </motion.div>
          )}

          {step === 'options' && (
            <motion.div key="options" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="space-y-6">
              <DebtSummary />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-primary mb-2">Step 2 • Choose how to settle</p>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { id: 'full', title: 'Pay full balance', body: 'Pay SAR 18,450 today and close your case immediately.', accent: 'Best for case closure' },
                    { id: 'plan', title: 'Installment plan', body: 'Configure a payment plan with up to 12 monthly instalments.', accent: 'Most flexible' },
                    { id: 'settlement', title: 'Settlement offer', body: 'Request a discounted lump-sum settlement — subject to approval.', accent: 'Requires creditor approval' },
                  ].map((o) => (
                    <button key={o.id} onClick={() => setOption(o.id)} className={cn('text-left surface-card p-5 transition-all hover:shadow-lift', option === o.id && 'ring-2 ring-primary border-primary/40')}>
                      <Badge variant="outline" className="text-[10px] mb-2">{o.accent}</Badge>
                      <p className="text-base font-semibold">{o.title}</p>
                      <p className="text-sm text-muted-foreground mt-1.5">{o.body}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStep('verify')}><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setStep('plan')}>Continue<ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              </div>
            </motion.div>
          )}

          {step === 'plan' && (
            <motion.div key="plan" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="space-y-6">
              <DebtSummary />
              <div className="surface-card p-6 grid md:grid-cols-2 gap-6">
                <div className="space-y-5">
                  <p className="text-[10px] uppercase tracking-widest text-primary">Step 3 • Configure your plan</p>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="text-muted-foreground">Down payment</span>
                      <span className="font-mono-num font-semibold">{downPct}% • {formatSAR(downPayment)}</span>
                    </div>
                    <Slider value={[downPct]} onValueChange={(v) => setDownPct(v[0])} min={0} max={50} step={5} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="text-muted-foreground">Installments</span>
                      <span className="font-mono-num font-semibold">{installments} months</span>
                    </div>
                    <Slider value={[installments]} onValueChange={(v) => setInstallments(v[0])} min={2} max={12} step={1} />
                  </div>
                  <div className="p-4 rounded-lg bg-primary/5 border border-primary/30">
                    <p className="text-xs text-muted-foreground">Monthly instalment</p>
                    <p className="font-mono-num text-3xl font-semibold text-foreground mt-0.5">{formatSAR(monthly)}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">Total {formatSAR(downPayment + monthly * installments)} over {installments + 1} payments</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Installment schedule</p>
                  <div className="max-h-80 overflow-y-auto space-y-1 pr-1 scrollbar-thin">
                    {Array.from({ length: installments }).map((_, i) => (
                      <div key={i} className="flex items-center justify-between text-xs px-3 py-2 rounded-md bg-muted/60 border border-border">
                        <span className="text-muted-foreground">Instalment {i + 1}</span>
                        <span>{formatDate(new Date(Date.now() + (i + 1) * 30 * 86400000).toISOString())}</span>
                        <span className="font-mono-num font-semibold">{formatSAR(monthly)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStep('options')}><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setStep('review')}>Review & pay<ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              </div>
            </motion.div>
          )}

          {step === 'review' && (
            <motion.div key="review" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="space-y-6">
              <div className="surface-card p-6">
                <p className="text-[10px] uppercase tracking-widest text-primary">Step 4 • Review & pay first instalment</p>
                <h2 className="text-xl font-semibold mt-1">Your settlement plan</h2>
                <div className="mt-4 grid md:grid-cols-3 gap-3">
                  <Box k="Down payment today" v={formatSAR(downPayment)} accent />
                  <Box k="Monthly instalment" v={formatSAR(monthly)} />
                  <Box k="Number of instalments" v={`${installments}`} />
                </div>
                <div className="mt-5 p-4 rounded-lg bg-onyx text-background">
                  <p className="text-[10px] uppercase tracking-widest text-primary mb-1">Pay via SADAD</p>
                  <p className="text-sm">Bill reference <span className="font-mono-num text-primary">SAD-{77000 + Math.floor(Math.random() * 999)}</span> will be valid for 48 hours.</p>
                  <p className="text-[11px] text-background/60 mt-1">No real payment will be processed in this prototype.</p>
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                  <Button variant="outline" onClick={() => setStep('plan')}><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
                  <Button className="bg-success text-success-foreground hover:bg-success/90" onClick={() => { toast.success('Payment confirmed', { description: `SAR ${downPayment.toLocaleString()} • case updated` }); setStep('done'); }}>
                    <CreditCard className="h-4 w-4 mr-1.5" />Pay {formatSAR(downPayment)} via SADAD
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {step === 'done' && (
            <motion.div key="done" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="surface-card p-10 text-center">
              <div className="h-16 w-16 mx-auto rounded-full bg-success/15 text-success flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-semibold mt-4">Payment received</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Your first instalment of {formatSAR(downPayment)} has been confirmed. A receipt has been sent to your WhatsApp. Your case is now <Badge variant="outline" className="text-success border-success/40 bg-success/10">Payment Plan Active</Badge></p>
              <div className="mt-6 grid md:grid-cols-3 gap-3 max-w-2xl mx-auto">
                <Box k="Next due" v={formatDate(new Date(Date.now() + 30 * 86400000).toISOString())} />
                <Box k="Remaining balance" v={formatSAR(CASE.amount - downPayment)} accent />
                <Box k="Case status" v="Payment Plan Active" />
              </div>
              <div className="mt-6 flex justify-center gap-2">
                <Button variant="outline" onClick={() => setStep('verify')}>Start over</Button>
                <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90" onClick={() => navigate('/app/command-center')}>Back to Alwasl Recovery OS</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer className="border-t border-border bg-card mt-10">
        <div className="max-w-5xl mx-auto px-5 py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <div>Alwasl National — Saudi Central Bank (SAMA) supervised • PDPL aware</div>
          <div className="flex items-center gap-3">
            <Globe className="h-3.5 w-3.5" /> alwaslsaudi.com • 920013620
          </div>
        </div>
      </footer>
    </div>
  );
}

const DebtSummary = () => (
  <div className="surface-card p-5 grid md:grid-cols-4 gap-3">
    <div className="md:col-span-2">
      <p className="text-[10px] uppercase tracking-widest text-primary">Account summary</p>
      <h3 className="text-lg font-semibold mt-0.5">{CASE.product} — {CASE.client}</h3>
      <p className="text-xs text-muted-foreground mt-1">{CASE.caseId} • {CASE.delinquencyDays} days delinquent</p>
    </div>
    <Box k="Outstanding" v={formatSAR(CASE.amount)} accent />
    <Box k="Next action" v="Choose a settlement option" />
  </div>
);

const Box = ({ k, v, accent }) => (
  <div className={cn('p-3 rounded-lg border', accent ? 'border-primary/40 bg-primary/5' : 'border-border bg-muted/40')}>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
    <p className={cn('font-mono-num text-lg font-semibold mt-0.5', accent && 'text-foreground')}>{v}</p>
  </div>
);
